/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo, useEffect } from 'react';
import { useWBL } from '../../context/WBLContext';
import { FerpaOverrideModal } from '../Common/FerpaOverrideModal';
import { PasswordManagementModal } from '../Common/PasswordManagementModal';
import { UserProfileModal } from './UserProfileModal';
import { FerpaReconciliationModal } from './FerpaReconciliationModal';
import { 
  Search, Globe, Users, ChevronDown, ChevronUp, 
  Check, RefreshCw, X, UserPlus, AlertCircle, ShieldAlert, ShieldCheck, 
  School, GraduationCap, Mail, Clock, AlertTriangle, Crown, Table, 
  LayoutGrid, ArrowUpRight, CheckCircle2, UserCheck, Eye, Filter, RotateCcw
} from 'lucide-react';
import { UserProfile } from '../../types';
import { StudentAvatar } from '../Common/StudentAvatar';
import { formatLastNameFirst, sortStudentsByLastName } from '../../utils/studentUtils';
import { calculateAge, formatDateOfBirth } from '../../utils/timeUtils';
import { useInAppDialog } from '../Common/InAppDialog';

export const UsersTabContent: React.FC = () => {
  const { 
    users, 
    tenants, 
    updateUserProfile, 
    allSchools,
    allDistricts,
    activeUser,
    activeTenant,
    createAuthenticatedUser,
    adminOverrideFerpaConsent,
    resendStudentFerpaConsent,
    delegateOrgLead
  } = useWBL();

  const { showAlert, showConfirm, showToast } = useInAppDialog();

  const [overrideStudentId, setOverrideStudentId] = useState<string | null>(null);
  const [selectedPasswordUser, setSelectedPasswordUser] = useState<UserProfile | null>(null);
  const [selectedProfileUser, setSelectedProfileUser] = useState<UserProfile | null>(null);
  const [showReconciliationModal, setShowReconciliationModal] = useState(false);

  const [activeUserTab, setActiveUserTab] = useState<'all' | 'students' | 'advisors' | 'parents' | 'organizations' | 'admins'>('all');
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');
  const [groupByHighSchool, setGroupByHighSchool] = useState<boolean>(true);
  const [collapsedSchools, setCollapsedSchools] = useState<Record<string, boolean>>({});

  const [searchUserQuery, setSearchUserQuery] = useState('');
  const [selectedRegionFilter, setSelectedRegionFilter] = useState('all');
  const [showProvisionForm, setShowProvisionForm] = useState(false);

  // Column specific filter states
  const [columnFilterContact, setColumnFilterContact] = useState('');
  const [columnFilterRole, setColumnFilterRole] = useState<'all' | 'student' | 'advisor' | 'parent' | 'organization' | 'admin'>('all');
  const [columnFilterRegion, setColumnFilterRegion] = useState('all');
  const [columnFilterAffiliation, setColumnFilterAffiliation] = useState('');
  const [columnFilterFerpa, setColumnFilterFerpa] = useState<'all' | 'granted' | 'sent' | 'pending'>('all');
  const [columnFilterAge, setColumnFilterAge] = useState<'all' | 'minor' | 'adult'>('all');
  const [columnFilterVerified, setColumnFilterVerified] = useState<'all' | 'verified' | 'unverified'>('all');

  const [openColumnFilter, setOpenColumnFilter] = useState<string | null>(null);

  const hasActiveColumnFilters = 
    !!columnFilterContact.trim() || 
    columnFilterRole !== 'all' || 
    columnFilterRegion !== 'all' || 
    !!columnFilterAffiliation.trim() || 
    columnFilterFerpa !== 'all' || 
    columnFilterAge !== 'all' || 
    columnFilterVerified !== 'all';

  const resetAllColumnFilters = () => {
    setColumnFilterContact('');
    setColumnFilterRole('all');
    setColumnFilterRegion('all');
    setColumnFilterAffiliation('');
    setColumnFilterFerpa('all');
    setColumnFilterAge('all');
    setColumnFilterVerified('all');
  };

  // Student specific editing & resend states
  const [resendingFerpa, setResendingFerpa] = useState<Record<string, boolean>>({});
  const [isReconcilingFerpa, setIsReconcilingFerpa] = useState(false);

  // Provisioning form states
  const [provisionName, setProvisionName] = useState('');
  const [provisionEmail, setProvisionEmail] = useState('');
  const [provisionPassword, setProvisionPassword] = useState('');
  const [provisionRole, setProvisionRole] = useState<'student' | 'advisor' | 'organization' | 'regional_admin' | 'superadmin' | 'parent'>('student');
  const [provisionTenantId, setProvisionTenantId] = useState('global');
  const [provisionDistrictId, setProvisionDistrictId] = useState('');
  const [provisionSchoolId, setProvisionSchoolId] = useState('');
  const [provisionOrgName, setProvisionOrgName] = useState('');
  const [provisionLoading, setProvisionLoading] = useState(false);
  const [provisionError, setProvisionError] = useState('');
  const [provisionSuccess, setProvisionSuccess] = useState('');



  const formatDateTime = (iso?: string | null) => {
    if (!iso) return 'Not recorded';
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return 'Invalid date';
      return d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return 'Not recorded';
    }
  };

  const handleUpdateUser = async (uid: string, updates: Partial<UserProfile>) => {
    try {
      await updateUserProfile(uid, updates);
      if (selectedProfileUser && selectedProfileUser.uid === uid) {
        setSelectedProfileUser({
          ...selectedProfileUser,
          ...updates
        });
      }
    } catch (err: any) {
      console.error(err);
      showToast({ title: 'Update Failed', message: err.message || 'Error updating user profile.', type: 'danger' });
    }
  };

  const handleResendFerpa = async (student: UserProfile, customEmail?: string) => {
    const uid = student.uid || (student as any).id || '';
    setResendingFerpa(prev => ({ ...prev, [uid]: true }));
    try {
      const res = await resendStudentFerpaConsent(uid, customEmail);
      showToast({
        title: 'Consent Request Dispatched',
        message: `Official FERPA consent reminder successfully sent to ${res.recipientEmail}.`,
        type: 'success'
      });
      if (selectedProfileUser && selectedProfileUser.uid === uid) {
        setSelectedProfileUser({
          ...selectedProfileUser,
          ferpaConsentSentAt: new Date().toISOString(),
          ferpaConsentRecipientEmail: res.recipientEmail,
          parentEmail: res.recipientEmail
        });
      }
    } catch (err: any) {
      showToast({
        title: 'Dispatch Failed',
        message: err.message || 'Error dispatching FERPA reminder.',
        type: 'danger'
      });
    } finally {
      setResendingFerpa(prev => ({ ...prev, [uid]: false }));
    }
  };

  const handleReconcileSingleStudent = async (studentId: string) => {
    setIsReconcilingFerpa(true);
    try {
      const res = await fetch('/api/reconcile-ferpa-consents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ studentIds: [studentId] })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to reconcile student consent.');

      showToast({
        title: 'Student FERPA Synchronized',
        message: 'Successfully corrected student FERPA entry in Firestore.',
        type: 'success'
      });

      // Update in local state
      const updatedUser = (users || []).find(u => u.uid === studentId || (u as any).id === studentId);
      if (updatedUser) {
        setSelectedProfileUser({
          ...updatedUser,
          ferpaConsent: {
            granted: true,
            date: new Date().toISOString(),
            version: '1.0',
            signatureType: 'type',
            signatureValue: updatedUser.name || 'Parent / Legal Guardian'
          },
          ferpaConsentDate: new Date().toISOString()
        });
      }
    } catch (err: any) {
      showToast({
        title: 'Reconciliation Failed',
        message: err.message || 'Error updating Firestore.',
        type: 'danger'
      });
    } finally {
      setIsReconcilingFerpa(false);
    }
  };

  const handleProvisionAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setProvisionError('');
    setProvisionSuccess('');
    setProvisionLoading(true);

    try {
      if (!provisionName.trim()) throw new Error('Full Name is required.');
      if (!provisionEmail.trim()) throw new Error('Email Address is required.');
      if (!provisionPassword.trim() || provisionPassword.length < 6) throw new Error('Password must be at least 6 characters.');

      let schoolName = '';
      if (provisionSchoolId) {
        const found = (allSchools || []).find(s => s && s.id === provisionSchoolId);
        schoolName = found ? found.name : '';
      }

      let districtName = '';
      if (provisionDistrictId) {
        const found = (allDistricts || []).find(d => d && d.id === provisionDistrictId);
        districtName = found ? found.name : '';
      }

      await createAuthenticatedUser(
        provisionEmail.trim().toLowerCase(),
        provisionPassword.trim(),
        provisionRole,
        {
          name: provisionName.trim(),
          tenantId: provisionTenantId,
          districtId: provisionDistrictId || undefined,
          district: districtName || undefined,
          schoolId: provisionSchoolId || undefined,
          school: schoolName || undefined,
          orgName: provisionRole === 'organization' ? provisionOrgName.trim() : undefined,
          isOrgLead: provisionRole === 'organization' ? true : undefined,
          verified: true
        }
      );

      setProvisionSuccess(`Successfully provisioned ${provisionRole.toUpperCase()} account for ${provisionEmail.trim()}!`);
      setProvisionName('');
      setProvisionEmail('');
      setProvisionPassword('');
      setProvisionSchoolId('');
      setProvisionDistrictId('');
      setProvisionOrgName('');
    } catch (err: any) {
      console.error(err);
      setProvisionError(err.message || 'Failed to provision account.');
    } finally {
      setProvisionLoading(false);
    }
  };

  // Global scope indicator: active tenant is global, user is superadmin, or region context is global/all
  const isGlobalScope = useMemo(() => {
    if (!activeUser) return true;
    if (activeUser.role === 'superadmin') return true;
    if (activeTenant?.id === 'global') return true;
    if (activeUser.tenantId === 'global' || activeUser.tenantId === 'all') return true;
    if (activeUser.extendedRegions?.includes('global') || activeUser.extendedRegions?.includes('all')) return true;
    return false;
  }, [activeUser, activeTenant]);

  // Compute active region administrative scope
  const adminRegions = useMemo(() => {
    if (!activeUser) return [];
    if (isGlobalScope) return ['all', 'global', ...(tenants || []).map(t => t.id)];
    const regions = [activeUser.tenantId, ...(activeUser.extendedRegions || [])].filter(Boolean);
    return Array.from(new Set(regions));
  }, [activeUser, isGlobalScope, tenants]);

  const formatUserDisplayName = (name: string, user?: UserProfile): string => {
    if (!name) return 'Unnamed User';
    if (user && user.role !== 'student') return name;
    return formatLastNameFirst(name);
  };

  // Filtered & sorted users list
  const filteredAndSortedUsers = useMemo(() => {
    const list = (users || []).filter(user => {
      if (!user || !user.uid) return false;

      // Filter by role tab
      if (activeUserTab === 'students' && user.role !== 'student') return false;
      if (activeUserTab === 'advisors' && user.role !== 'advisor') return false;
      if (activeUserTab === 'parents' && user.role !== 'parent') return false;
      if (activeUserTab === 'organizations' && user.role !== 'organization') return false;
      if (activeUserTab === 'admins' && !['superadmin', 'regional_admin'].includes(user.role)) return false;

      // Filter by administrative scope if regional_admin (unless Global is selected)
      if (activeUser?.role === 'regional_admin' && !isGlobalScope) {
        if (user.role === 'superadmin') return false;
        const userRegions = [user.tenantId, ...(user.extendedRegions || [])].filter(Boolean);
        const hasOverlap = userRegions.some(r => adminRegions.includes(r));
        if (!hasOverlap) return false;
      }

      // Filter by selected region
      if (selectedRegionFilter !== 'all' && selectedRegionFilter !== 'global') {
        const userTenant = user.tenantId || 'global';
        const userRegions = [user.tenantId, ...(user.extendedRegions || [])].filter(Boolean);
        if (userTenant !== selectedRegionFilter && !userRegions.includes(selectedRegionFilter)) return false;
      }

      if (searchUserQuery.trim()) {
        const query = searchUserQuery.toLowerCase();
        const matchesName = user.name && user.name.toLowerCase().includes(query);
        const matchesEmail = user.email && user.email.toLowerCase().includes(query);
        const matchesSchool = user.school && user.school.toLowerCase().includes(query);
        const matchesDistrict = user.district && user.district.toLowerCase().includes(query);
        const matchesOrg = user.orgName && user.orgName.toLowerCase().includes(query);
        const display = formatUserDisplayName(user.name, user).toLowerCase();
        if (!(matchesName || matchesEmail || matchesSchool || matchesDistrict || matchesOrg || display.includes(query))) {
          return false;
        }
      }

      // Column filters
      if (columnFilterContact.trim()) {
        const q = columnFilterContact.toLowerCase().trim();
        const matchesName = user.name && user.name.toLowerCase().includes(q);
        const matchesEmail = user.email && user.email.toLowerCase().includes(q);
        if (!matchesName && !matchesEmail) return false;
      }

      if (columnFilterRole !== 'all') {
        const r = user.role || 'student';
        if (columnFilterRole === 'admin') {
          if (!['superadmin', 'regional_admin'].includes(r)) return false;
        } else if (r !== columnFilterRole) {
          return false;
        }
      }

      if (columnFilterRegion !== 'all') {
        const userTenant = user.tenantId || 'global';
        const userRegions = [user.tenantId, ...(user.extendedRegions || [])].filter(Boolean);
        if (userTenant !== columnFilterRegion && !userRegions.includes(columnFilterRegion)) return false;
      }

      if (columnFilterAffiliation.trim()) {
        const q = columnFilterAffiliation.toLowerCase().trim();
        const matchesSchool = user.school && user.school.toLowerCase().includes(q);
        const matchesDistrict = user.district && user.district.toLowerCase().includes(q);
        const matchesOrg = user.orgName && user.orgName.toLowerCase().includes(q);
        if (!matchesSchool && !matchesDistrict && !matchesOrg) return false;
      }

      if (columnFilterFerpa !== 'all') {
        const info = getUserFerpaInfo(user);
        if (columnFilterFerpa === 'granted' && !info.isGranted) return false;
        if (columnFilterFerpa === 'sent' && (!info.isSent || info.isGranted)) return false;
        if (columnFilterFerpa === 'pending' && (info.isGranted || info.isSent)) return false;
      }

      if (columnFilterAge !== 'all') {
        const age = calculateAge(user.dateOfBirth);
        if (columnFilterAge === 'minor' && (age === null || age >= 18)) return false;
        if (columnFilterAge === 'adult' && (age === null || age < 18)) return false;
      }

      if (columnFilterVerified !== 'all') {
        const isVer = !!user.verified;
        if (columnFilterVerified === 'verified' && !isVer) return false;
        if (columnFilterVerified === 'unverified' && isVer) return false;
      }

      return true;
    });

    return sortStudentsByLastName(list, (u: UserProfile) => u.name, (u: UserProfile) => u);
  }, [users, searchUserQuery, selectedRegionFilter, activeUser, adminRegions, activeUserTab, isGlobalScope, columnFilterContact, columnFilterRole, columnFilterRegion, columnFilterAffiliation, columnFilterFerpa, columnFilterAge, columnFilterVerified]);

  // Tab counts
  const tabCounts = useMemo(() => {
    const baseList = (users || []).filter(user => {
      if (!user || !user.uid) return false;
      if (activeUser?.role === 'regional_admin' && !isGlobalScope) {
        if (user.role === 'superadmin') return false;
        const userRegions = [user.tenantId, ...(user.extendedRegions || [])].filter(Boolean);
        if (!userRegions.some(r => adminRegions.includes(r))) return false;
      }
      if (selectedRegionFilter !== 'all' && selectedRegionFilter !== 'global') {
        const userTenant = user.tenantId || 'global';
        const userRegions = [user.tenantId, ...(user.extendedRegions || [])].filter(Boolean);
        if (userTenant !== selectedRegionFilter && !userRegions.includes(selectedRegionFilter)) return false;
      }
      return true;
    });

    return {
      all: baseList.length,
      students: baseList.filter(u => u.role === 'student').length,
      advisors: baseList.filter(u => u.role === 'advisor').length,
      parents: baseList.filter(u => u.role === 'parent').length,
      organizations: baseList.filter(u => u.role === 'organization').length,
      admins: baseList.filter(u => ['superadmin', 'regional_admin'].includes(u.role)).length,
    };
  }, [users, activeUser, adminRegions, selectedRegionFilter, isGlobalScope]);

  // Group students by High School for Card view
  const studentsGroupedBySchool = useMemo(() => {
    const studentUsers = filteredAndSortedUsers.filter(u => u.role === 'student');
    const map = new Map<string, {
      schoolName: string;
      schoolId: string;
      districtName: string;
      students: UserProfile[];
    }>();

    studentUsers.forEach(student => {
      const matchedSchool = (allSchools || []).find(s => s && (s.id === student.schoolId || s.name === student.school));
      const schoolName = student.school || matchedSchool?.name || 'Unassigned High School';
      const districtName = student.district || matchedSchool?.districtName || ((allDistricts || []).find(d => d && d.id === (student.districtId || matchedSchool?.districtId))?.name) || '';
      const schoolId = student.schoolId || matchedSchool?.id || '';
      
      const key = schoolName.trim();
      if (!map.has(key)) {
        map.set(key, { schoolName, schoolId, districtName, students: [] });
      }
      map.get(key)!.students.push(student);
    });

    return Array.from(map.values()).sort((a, b) => {
      if (a.schoolName === 'Unassigned High School') return 1;
      if (b.schoolName === 'Unassigned High School') return -1;
      return a.schoolName.localeCompare(b.schoolName);
    });
  }, [filteredAndSortedUsers, allSchools, allDistricts]);

  const toggleSchoolCollapse = (schoolName: string) => {
    setCollapsedSchools(prev => ({ ...prev, [schoolName]: !prev[schoolName] }));
  };

  const handleExpandAllSchools = () => setCollapsedSchools({});
  const handleCollapseAllSchools = () => {
    const allCollapsed: Record<string, boolean> = {};
    studentsGroupedBySchool.forEach(g => { allCollapsed[g.schoolName] = true; });
    setCollapsedSchools(allCollapsed);
  };

  // Helper for FERPA status per user
  const getUserFerpaInfo = (user: UserProfile) => {
    if (user.role === 'student') {
      const isDirectGranted = typeof user.ferpaConsent === 'object' ? user.ferpaConsent?.granted === true : user.ferpaConsent === true;
      const linkedParentWithConsent = (users || []).find(p => 
        p.role === 'parent' &&
        (
          p.childId === user.uid || 
          (Array.isArray(p.childrenIds) && p.childrenIds.includes(user.uid)) ||
          (user.parentId && p.uid === user.parentId) ||
          (!!p.email && !!user.parentEmail && p.email.toLowerCase().trim() === user.parentEmail.toLowerCase().trim()) ||
          (!!p.email && !!user.ferpaConsentRecipientEmail && p.email.toLowerCase().trim() === user.ferpaConsentRecipientEmail.toLowerCase().trim())
        ) &&
        (typeof p.ferpaConsent === 'object' ? p.ferpaConsent?.granted === true : p.ferpaConsent === true)
      );
      const isGranted = isDirectGranted || !!linkedParentWithConsent;
      const isSent = !!(user.ferpaConsentSentAt || user.ferpaConsentRecipientEmail);
      const recipient = user.ferpaConsentRecipientEmail || user.parentEmail || linkedParentWithConsent?.email || null;
      const date = user.ferpaConsentDate || 
        (typeof user.ferpaConsent === 'object' ? user.ferpaConsent?.date : '') || 
        linkedParentWithConsent?.ferpaConsentDate || 
        (typeof linkedParentWithConsent?.ferpaConsent === 'object' ? linkedParentWithConsent?.ferpaConsent?.date : '');
      return { isGranted, isSent, recipient, date, type: 'student' as const };
    }
    if (user.role === 'parent') {
      const isDirectGranted = typeof user.ferpaConsent === 'object' ? user.ferpaConsent?.granted === true : user.ferpaConsent === true;
      const linkedStudentWithConsent = (users || []).find(s => 
        s.role === 'student' &&
        (s.parentId === user.uid || s.parentEmail === user.email || s.ferpaConsentRecipientEmail === user.email) &&
        ((typeof s.ferpaConsent === 'object' ? s.ferpaConsent?.granted === true : s.ferpaConsent === true) || s.ferpaConsentDate)
      );
      const isSigned = isDirectGranted || !!linkedStudentWithConsent;
      const date = user.ferpaConsentDate || (linkedStudentWithConsent ? (typeof linkedStudentWithConsent.ferpaConsent === 'object' ? linkedStudentWithConsent.ferpaConsent?.date : linkedStudentWithConsent.ferpaConsentDate) : '');
      return { isGranted: isSigned, isSent: false, recipient: null, date, type: 'parent' as const };
    }
    return { isGranted: false, isSent: false, recipient: null, date: null, type: 'other' as const };
  };

  // Keep selectedProfileUser in sync with live users list from Firestore!
  useEffect(() => {
    if (selectedProfileUser) {
      const freshUser = (users || []).find(u => u.uid === selectedProfileUser.uid || (u as any).id === selectedProfileUser.uid);
      if (freshUser) {
        if (
          JSON.stringify(freshUser.ferpaConsent) !== JSON.stringify(selectedProfileUser.ferpaConsent) ||
          freshUser.parentId !== selectedProfileUser.parentId ||
          freshUser.parentEmail !== selectedProfileUser.parentEmail ||
          freshUser.ferpaConsentDate !== selectedProfileUser.ferpaConsentDate ||
          freshUser.ferpaConsentName !== selectedProfileUser.ferpaConsentName ||
          freshUser.verified !== selectedProfileUser.verified
        ) {
          setSelectedProfileUser(freshUser);
        }
      }
    }
  }, [users, selectedProfileUser]);

  return (
    <div className="w-full bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden p-4 sm:p-5 space-y-3.5">
      {/* Succinct & Collapsed Header Operations Toolbar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 pb-3 border-b border-slate-100">
        <div className="flex items-center gap-2.5 shrink-0">
          <h2 className="text-sm sm:text-base font-black text-[#011d3c] tracking-tight uppercase">User Directory</h2>
          <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-slate-100 text-slate-500">
            {filteredAndSortedUsers.length}
          </span>
        </div>

        <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
          {/* Region Filter */}
          <div className="relative shrink-0">
            <Globe className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
            <select
              value={selectedRegionFilter}
              onChange={(e) => setSelectedRegionFilter(e.target.value)}
              className="appearance-none pl-8 pr-7 py-1.5 border border-slate-200 rounded-lg text-xs font-bold text-slate-700 bg-white focus:ring-1 focus:ring-[#0094c8] outline-none uppercase cursor-pointer"
            >
              <option value="all">All Regions</option>
              {(isGlobalScope || activeUser?.role !== 'regional_admin') && <option value="global">Global</option>}
              {tenants
                .filter(t => {
                  if (t.id === 'global') return false;
                  if (activeUser?.role === 'regional_admin' && !isGlobalScope) {
                    return adminRegions.includes(t.id);
                  }
                  return true;
                })
                .map(t => (
                  <option key={t.id} value={t.id}>{(t.name || t.id).toUpperCase()}</option>
                ))}
            </select>
            <ChevronDown className="w-3 h-3 text-slate-400 absolute right-2 top-2.5 pointer-events-none" />
          </div>

          {/* Search bar */}
          <div className="relative flex-1 min-w-[200px] w-full">
            <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search users by name, email, school, district, organization..."
              value={searchUserQuery}
              onChange={(e) => setSearchUserQuery(e.target.value)}
              className="pl-8 pr-3 py-1.5 border border-slate-200 rounded-lg text-xs focus:ring-1 focus:ring-[#0094c8] outline-none placeholder-slate-400 bg-white w-full"
            />
          </div>

          {/* Reconcile FERPA Consents Button */}
          <button
            type="button"
            disabled={isReconcilingFerpa}
            onClick={() => setShowReconciliationModal(true)}
            title="Scan Firestore to match students whose parents granted FERPA consent"
            className="flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-wider transition-all cursor-pointer bg-emerald-50 text-emerald-800 hover:bg-emerald-100 border border-emerald-300 shrink-0 shadow-3xs"
          >
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
            <span>Reconcile</span>
          </button>

          {/* Provision Button */}
          <button
            type="button"
            onClick={() => setShowProvisionForm(prev => !prev)}
            className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-wider transition-all cursor-pointer shrink-0 ${
              showProvisionForm 
                ? 'bg-[#011d3c] text-white' 
                : 'bg-[#0094c8]/10 text-[#0094c8] hover:bg-[#0094c8]/25 border border-[#0094c8]/25'
            }`}
          >
            <UserPlus className="w-3.5 h-3.5" />
            <span>Provision</span>
          </button>
        </div>
      </div>

      {/* Streamlined Role Filter Pills & View Switcher */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 pb-2">
        <div className="flex flex-wrap items-center gap-1.5">
          {[
            { id: 'all', label: 'All', count: tabCounts.all },
            { id: 'students', label: 'Students', count: tabCounts.students },
            { id: 'advisors', label: 'Advisors', count: tabCounts.advisors },
            { id: 'parents', label: 'Parents', count: tabCounts.parents },
            { id: 'organizations', label: 'Employers', count: tabCounts.organizations },
            { id: 'admins', label: 'Admins', count: tabCounts.admins },
          ].map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveUserTab(tab.id as any)}
              className={`px-2.5 py-1 rounded-lg text-[11px] font-black uppercase tracking-wider transition-all cursor-pointer flex items-center space-x-1.5 ${
                activeUserTab === tab.id
                  ? 'bg-[#011d3c] text-white shadow-2xs'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <span>{tab.label}</span>
              <span className={`px-1.5 py-0.2 rounded-full text-[9px] font-bold ${activeUserTab === tab.id ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-600'}`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* View Mode Switcher */}
        <div className="flex items-center gap-0.5 bg-slate-100 p-0.5 rounded-lg self-start sm:self-auto shrink-0">
          <button
            type="button"
            onClick={() => setViewMode('table')}
            title="Table View"
            className={`p-1.5 rounded-md text-xs font-bold transition-all cursor-pointer ${
              viewMode === 'table' ? 'bg-white text-[#011d3c] shadow-3xs' : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            <Table className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setViewMode('cards')}
            title="Card View"
            className={`p-1.5 rounded-md text-xs font-bold transition-all cursor-pointer ${
              viewMode === 'cards' ? 'bg-white text-[#011d3c] shadow-3xs' : 'text-slate-500 hover:text-slate-900'
            }`}
          >
            <LayoutGrid className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Provision Account Panel */}
      {showProvisionForm && (
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-4 animate-fade-in">
          <div className="flex items-center justify-between pb-3 border-b border-slate-200">
            <div className="flex items-center space-x-2">
              <UserPlus className="w-5 h-5 text-[#0094c8]" />
              <h3 className="text-sm font-black uppercase tracking-wider text-[#011d3c]">Provision New User Account</h3>
            </div>
            <button
              type="button"
              onClick={() => {
                setShowProvisionForm(false);
                setProvisionError('');
                setProvisionSuccess('');
                setProvisionPassword('');
              }}
              className="text-slate-400 hover:text-slate-600 transition-colors p-1"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {provisionError && (
            <div className="flex items-center space-x-2 bg-rose-50 border border-rose-100 text-rose-800 p-3 rounded-xl text-xs font-semibold">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{provisionError}</span>
            </div>
          )}

          {provisionSuccess && (
            <div className="flex items-center space-x-2 bg-emerald-50 border border-emerald-100 text-emerald-800 p-3 rounded-xl text-xs font-semibold">
              <Check className="w-4 h-4 shrink-0" />
              <span>{provisionSuccess}</span>
            </div>
          )}

          <form onSubmit={handleProvisionAccount} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase text-slate-400">Full Name</label>
              <input
                type="text"
                required
                placeholder="e.g. Sam Student"
                value={provisionName}
                onChange={(e) => setProvisionName(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs focus:ring-1 focus:ring-[#0094c8] outline-none"
              />
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase text-slate-400">Email Address</label>
              <input
                type="email"
                required
                placeholder="e.g. sam@example.com"
                value={provisionEmail}
                onChange={(e) => setProvisionEmail(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs focus:ring-1 focus:ring-[#0094c8] outline-none"
              />
            </div>

            <div className="space-y-1">
              <div className="flex justify-between items-center">
                <label className="text-[10px] font-black uppercase text-slate-400">Temporary Password</label>
                <button
                  type="button"
                  onClick={() => {
                    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
                    let pass = '';
                    for (let i = 0; i < 12; i++) {
                      pass += chars.charAt(Math.floor(Math.random() * chars.length));
                    }
                    setProvisionPassword(pass);
                  }}
                  className="text-[9px] font-bold text-[#0094c8] hover:underline uppercase cursor-pointer"
                >
                  Generate Password
                </button>
              </div>
              <input
                type="text"
                required
                placeholder="Temporary Password"
                value={provisionPassword}
                onChange={(e) => setProvisionPassword(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs focus:ring-1 focus:ring-[#0094c8] outline-none font-mono"
              />
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase text-slate-400">Account Role</label>
              <div className="relative">
                <select
                  value={provisionRole}
                  onChange={(e) => {
                    const role = e.target.value as any;
                    setProvisionRole(role);
                    setProvisionDistrictId('');
                    setProvisionSchoolId('');
                    setProvisionOrgName('');
                  }}
                  className="w-full appearance-none bg-white border border-slate-200 text-xs px-3 py-2 rounded-xl outline-none focus:ring-1 focus:ring-[#0094c8] uppercase font-bold"
                >
                  <option value="student">STUDENT</option>
                  <option value="advisor">ADVISOR / TEACHER</option>
                  <option value="organization">EMPLOYER REPRESENTATIVE</option>
                  <option value="regional_admin">REGIONAL ADMINISTRATOR</option>
                  {activeUser?.role !== 'regional_admin' && <option value="superadmin">SUPERADMINISTRATOR</option>}
                  <option value="parent">PARENT / GUARDIAN</option>
                </select>
                <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-2.5 pointer-events-none" />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase text-slate-400">Primary Region Authority</label>
              <div className="relative">
                <select
                  value={provisionTenantId}
                  disabled={activeUser?.role === 'regional_admin' && !isGlobalScope && adminRegions.length <= 1}
                  onChange={(e) => {
                    setProvisionTenantId(e.target.value);
                    setProvisionDistrictId('');
                    setProvisionSchoolId('');
                  }}
                  className="w-full appearance-none bg-white disabled:bg-slate-100 border border-slate-200 text-xs px-3 py-2 rounded-xl outline-none focus:ring-1 focus:ring-[#0094c8] uppercase font-bold"
                >
                  {(isGlobalScope || activeUser?.role !== 'regional_admin') && <option value="global">GLOBAL</option>}
                  {tenants
                    .filter(t => {
                      if (t.id === 'global') return false;
                      if (activeUser?.role === 'regional_admin' && !isGlobalScope) {
                        return adminRegions.includes(t.id);
                      }
                      return true;
                    })
                    .map(t => (
                      <option key={t.id} value={t.id}>{(t.name || t.id).toUpperCase()}</option>
                    ))}
                </select>
                <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-2.5 pointer-events-none" />
              </div>
            </div>

            {['student', 'advisor', 'parent'].includes(provisionRole) && (
              <>
                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase text-slate-400">School District</label>
                  <div className="relative">
                    <select
                      value={provisionDistrictId}
                      onChange={(e) => {
                        setProvisionDistrictId(e.target.value);
                        setProvisionSchoolId('');
                      }}
                      className="w-full appearance-none bg-white border border-slate-200 text-xs px-3 py-2 rounded-xl outline-none focus:ring-1 focus:ring-[#0094c8] uppercase font-bold"
                    >
                      <option value="">None / Unassigned</option>
                      {(allDistricts || [])
                        .filter(d => d && (provisionTenantId === 'global' || d.regionId === provisionTenantId))
                        .map(d => (
                          <option key={d.id} value={d.id}>{(d.name || '').toUpperCase()}</option>
                        ))}
                    </select>
                    <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-2.5 pointer-events-none" />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-black uppercase text-slate-400">High School</label>
                  <div className="relative">
                    <select
                      value={provisionSchoolId}
                      onChange={(e) => setProvisionSchoolId(e.target.value)}
                      className="w-full appearance-none bg-white border border-slate-200 text-xs px-3 py-2 rounded-xl outline-none focus:ring-1 focus:ring-[#0094c8] uppercase font-bold"
                    >
                      <option value="">None / Unassigned</option>
                      {(allSchools || [])
                        .filter(s => s && (provisionTenantId === 'global' || s.regionId === provisionTenantId))
                        .filter(s => !provisionDistrictId || s.districtId === provisionDistrictId)
                        .map(s => (
                          <option key={s.id} value={s.id}>{(s.name || '').toUpperCase()}</option>
                        ))}
                    </select>
                    <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-2.5 pointer-events-none" />
                  </div>
                </div>
              </>
            )}

            {provisionRole === 'organization' && (
              <div className="space-y-1 md:col-span-2">
                <label className="text-[10px] font-black uppercase text-slate-400">Employer Organization Name</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Apex Tech Solutions"
                  value={provisionOrgName}
                  onChange={(e) => setProvisionOrgName(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs focus:ring-1 focus:ring-[#0094c8] outline-none"
                />
              </div>
            )}

            <div className="md:col-span-2 flex items-center justify-end space-x-3 pt-3 border-t border-slate-200 mt-2">
              <button
                type="button"
                onClick={() => {
                  setShowProvisionForm(false);
                  setProvisionError('');
                  setProvisionSuccess('');
                  setProvisionPassword('');
                }}
                className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-all uppercase cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={provisionLoading}
                className="bg-[#0094c8] hover:bg-[#007ba6] disabled:opacity-50 text-white px-4 py-2 rounded-xl text-xs font-black uppercase transition-all flex items-center space-x-2 cursor-pointer shadow-sm"
              >
                {provisionLoading ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    <span>Provisioning...</span>
                  </>
                ) : (
                  <>
                    <UserPlus className="w-3.5 h-3.5" />
                    <span>Provision Account</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Main Content Area: Table View vs Card View */}
      {filteredAndSortedUsers.length === 0 ? (
        <div className="py-16 text-center text-xs text-slate-400 uppercase font-black tracking-wider bg-slate-50/50 rounded-2xl border border-dashed border-slate-200">
          No matching user accounts registered in this scope.
        </div>
      ) : viewMode === 'table' ? (
        /* TABLE VIEW WITH COLUMN HEADERS */
        <div className="border border-slate-200 rounded-2xl overflow-hidden shadow-2xs bg-white">
          <div className="overflow-x-auto">
            {openColumnFilter && <div className="fixed inset-0 z-20" onClick={() => setOpenColumnFilter(null)} />}
            <table className="w-full text-left border-collapse">
              <thead className="bg-slate-50/90 border-b border-slate-200">
                <tr className="text-[10px] font-black uppercase tracking-wider text-slate-500 select-none">
                  {/* 1. USER / CONTACT */}
                  <th className="py-3 px-4 relative">
                    <div className="flex items-center justify-between gap-1">
                      <span>User / Contact</span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setOpenColumnFilter(openColumnFilter === "contact" ? null : "contact"); }}
                        className={`p-1 rounded-md transition-all cursor-pointer ${
                          columnFilterContact ? "bg-[#0094c8] text-white shadow-2xs" : "text-slate-400 hover:text-slate-700 hover:bg-slate-200/60"
                        }`}
                        title="Filter User / Contact"
                      >
                        <Filter className="w-3 h-3" />
                      </button>
                    </div>
                    {openColumnFilter === "contact" && (
                      <div className="absolute left-2 top-full mt-1 w-60 bg-white border border-slate-200 rounded-xl shadow-xl p-3 z-30 text-xs normal-case font-normal text-slate-700 animate-scale-up">
                        <div className="font-bold text-[10px] uppercase tracking-wider text-slate-400 mb-2 flex items-center justify-between">
                          <span>Filter Contact</span>
                          {columnFilterContact && (
                            <button type="button" onClick={() => setColumnFilterContact("")} className="text-[#0094c8] hover:underline text-[10px] font-bold">Clear</button>
                          )}
                        </div>
                        <div className="relative">
                          <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2.5" />
                          <input
                            type="text"
                            placeholder="Filter name or email..."
                            value={columnFilterContact}
                            onChange={(e) => setColumnFilterContact(e.target.value)}
                            className="w-full pl-8 pr-2.5 py-1.5 border border-slate-200 rounded-lg text-xs outline-none focus:ring-1 focus:ring-[#0094c8]"
                            autoFocus
                          />
                        </div>
                      </div>
                    )}
                  </th>

                  {/* 2. ROLE */}
                  <th className="py-3 px-4 relative">
                    <div className="flex items-center justify-between gap-1">
                      <span>Role</span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setOpenColumnFilter(openColumnFilter === "role" ? null : "role"); }}
                        className={`p-1 rounded-md transition-all cursor-pointer ${
                          columnFilterRole !== "all" ? "bg-[#0094c8] text-white shadow-2xs" : "text-slate-400 hover:text-slate-700 hover:bg-slate-200/60"
                        }`}
                        title="Filter Role"
                      >
                        <Filter className="w-3 h-3" />
                      </button>
                    </div>
                    {openColumnFilter === "role" && (
                      <div className="absolute left-2 top-full mt-1 w-48 bg-white border border-slate-200 rounded-xl shadow-xl p-2 z-30 text-xs normal-case font-normal text-slate-700 space-y-1 animate-scale-up">
                        <div className="font-bold text-[10px] uppercase tracking-wider text-slate-400 px-2 py-1 flex items-center justify-between">
                          <span>Select Role</span>
                          {columnFilterRole !== "all" && (
                            <button type="button" onClick={() => setColumnFilterRole("all")} className="text-[#0094c8] hover:underline text-[10px] font-bold">Clear</button>
                          )}
                        </div>
                        {[
                          { id: "all", label: "All Roles" },
                          { id: "student", label: "Students" },
                          { id: "advisor", label: "Advisors" },
                          { id: "parent", label: "Parents" },
                          { id: "organization", label: "Employers" },
                          { id: "admin", label: "Admins" },
                        ].map(r => (
                          <button
                            key={r.id}
                            type="button"
                            onClick={() => { setColumnFilterRole(r.id as any); setOpenColumnFilter(null); }}
                            className={`w-full text-left px-2.5 py-1.5 rounded-lg flex items-center justify-between hover:bg-slate-50 transition-colors ${
                              columnFilterRole === r.id ? "font-bold text-[#0094c8] bg-sky-50" : "text-slate-600"
                            }`}
                          >
                            <span>{r.label}</span>
                            {columnFilterRole === r.id && <Check className="w-3.5 h-3.5 text-[#0094c8]" />}
                          </button>
                        ))}
                      </div>
                    )}
                  </th>

                  {/* 3. REGION */}
                  <th className="py-3 px-4 relative">
                    <div className="flex items-center justify-between gap-1">
                      <span>Region</span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setOpenColumnFilter(openColumnFilter === "region" ? null : "region"); }}
                        className={`p-1 rounded-md transition-all cursor-pointer ${
                          columnFilterRegion !== "all" ? "bg-[#0094c8] text-white shadow-2xs" : "text-slate-400 hover:text-slate-700 hover:bg-slate-200/60"
                        }`}
                        title="Filter Region"
                      >
                        <Filter className="w-3 h-3" />
                      </button>
                    </div>
                    {openColumnFilter === "region" && (
                      <div className="absolute left-2 top-full mt-1 w-52 bg-white border border-slate-200 rounded-xl shadow-xl p-2 z-30 text-xs normal-case font-normal text-slate-700 space-y-1 animate-scale-up max-h-60 overflow-y-auto">
                        <div className="font-bold text-[10px] uppercase tracking-wider text-slate-400 px-2 py-1 flex items-center justify-between">
                          <span>Filter Region</span>
                          {columnFilterRegion !== "all" && (
                            <button type="button" onClick={() => setColumnFilterRegion("all")} className="text-[#0094c8] hover:underline text-[10px] font-bold">Clear</button>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => { setColumnFilterRegion("all"); setOpenColumnFilter(null); }}
                          className={`w-full text-left px-2.5 py-1.5 rounded-lg flex items-center justify-between hover:bg-slate-50 ${
                            columnFilterRegion === "all" ? "font-bold text-[#0094c8] bg-sky-50" : "text-slate-600"
                          }`}
                        >
                          <span>All Regions</span>
                          {columnFilterRegion === "all" && <Check className="w-3.5 h-3.5 text-[#0094c8]" />}
                        </button>
                        <button
                          type="button"
                          onClick={() => { setColumnFilterRegion("global"); setOpenColumnFilter(null); }}
                          className={`w-full text-left px-2.5 py-1.5 rounded-lg flex items-center justify-between hover:bg-slate-50 ${
                            columnFilterRegion === "global" ? "font-bold text-[#0094c8] bg-sky-50" : "text-slate-600"
                          }`}
                        >
                          <span>GLOBAL</span>
                          {columnFilterRegion === "global" && <Check className="w-3.5 h-3.5 text-[#0094c8]" />}
                        </button>
                        {tenants.filter(t => t.id !== "global").map(t => (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => { setColumnFilterRegion(t.id); setOpenColumnFilter(null); }}
                            className={`w-full text-left px-2.5 py-1.5 rounded-lg flex items-center justify-between hover:bg-slate-50 ${
                              columnFilterRegion === t.id ? "font-bold text-[#0094c8] bg-sky-50" : "text-slate-600"
                            }`}
                          >
                            <span>{(t.name || t.id).toUpperCase()}</span>
                            {columnFilterRegion === t.id && <Check className="w-3.5 h-3.5 text-[#0094c8]" />}
                          </button>
                        ))}
                      </div>
                    )}
                  </th>

                  {/* 4. AFFILIATION / SCHOOL / ORG */}
                  <th className="py-3 px-4 relative">
                    <div className="flex items-center justify-between gap-1">
                      <span>Affiliation / School / Org</span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setOpenColumnFilter(openColumnFilter === "affiliation" ? null : "affiliation"); }}
                        className={`p-1 rounded-md transition-all cursor-pointer ${
                          columnFilterAffiliation ? "bg-[#0094c8] text-white shadow-2xs" : "text-slate-400 hover:text-slate-700 hover:bg-slate-200/60"
                        }`}
                        title="Filter Affiliation"
                      >
                        <Filter className="w-3 h-3" />
                      </button>
                    </div>
                    {openColumnFilter === "affiliation" && (
                      <div className="absolute left-2 top-full mt-1 w-64 bg-white border border-slate-200 rounded-xl shadow-xl p-3 z-30 text-xs normal-case font-normal text-slate-700 animate-scale-up space-y-2">
                        <div className="font-bold text-[10px] uppercase tracking-wider text-slate-400 flex items-center justify-between">
                          <span>Filter School / Org</span>
                          {columnFilterAffiliation && (
                            <button type="button" onClick={() => setColumnFilterAffiliation("")} className="text-[#0094c8] hover:underline text-[10px] font-bold">Clear</button>
                          )}
                        </div>
                        <div className="relative">
                          <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2.5" />
                          <input
                            type="text"
                            placeholder="Filter school, district, org..."
                            value={columnFilterAffiliation}
                            onChange={(e) => setColumnFilterAffiliation(e.target.value)}
                            className="w-full pl-8 pr-2.5 py-1.5 border border-slate-200 rounded-lg text-xs outline-none focus:ring-1 focus:ring-[#0094c8]"
                            autoFocus
                          />
                        </div>
                      </div>
                    )}
                  </th>

                  {/* 5. FERPA */}
                  <th className="py-3 px-3 text-center w-24 relative" title="FERPA Consent Status">
                    <div className="flex items-center justify-center gap-1">
                      <span>FERPA</span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setOpenColumnFilter(openColumnFilter === "ferpa" ? null : "ferpa"); }}
                        className={`p-1 rounded-md transition-all cursor-pointer ${
                          columnFilterFerpa !== "all" ? "bg-[#0094c8] text-white shadow-2xs" : "text-slate-400 hover:text-slate-700 hover:bg-slate-200/60"
                        }`}
                        title="Filter FERPA Status"
                      >
                        <Filter className="w-3 h-3" />
                      </button>
                    </div>
                    {openColumnFilter === "ferpa" && (
                      <div className="absolute right-0 top-full mt-1 w-52 bg-white border border-slate-200 rounded-xl shadow-xl p-2 z-30 text-xs normal-case font-normal text-slate-700 space-y-1 text-left animate-scale-up">
                        <div className="font-bold text-[10px] uppercase tracking-wider text-slate-400 px-2 py-1 flex items-center justify-between">
                          <span>FERPA Status</span>
                          {columnFilterFerpa !== "all" && (
                            <button type="button" onClick={() => setColumnFilterFerpa("all")} className="text-[#0094c8] hover:underline text-[10px] font-bold">Clear</button>
                          )}
                        </div>
                        {[
                          { id: "all", label: "All Statuses" },
                          { id: "granted", label: "Granted & Active" },
                          { id: "sent", label: "Consent Sent (Pending)" },
                          { id: "pending", label: "Pending / Not Sent" },
                        ].map(f => (
                          <button
                            key={f.id}
                            type="button"
                            onClick={() => { setColumnFilterFerpa(f.id as any); setOpenColumnFilter(null); }}
                            className={`w-full text-left px-2.5 py-1.5 rounded-lg flex items-center justify-between hover:bg-slate-50 ${
                              columnFilterFerpa === f.id ? "font-bold text-[#0094c8] bg-sky-50" : "text-slate-600"
                            }`}
                          >
                            <span>{f.label}</span>
                            {columnFilterFerpa === f.id && <Check className="w-3.5 h-3.5 text-[#0094c8]" />}
                          </button>
                        ))}
                      </div>
                    )}
                  </th>

                  {/* 6. AGE */}
                  <th className="py-3 px-3 text-center w-20 relative" title="Student Age Standing">
                    <div className="flex items-center justify-center gap-1">
                      <span>Age</span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setOpenColumnFilter(openColumnFilter === "age" ? null : "age"); }}
                        className={`p-1 rounded-md transition-all cursor-pointer ${
                          columnFilterAge !== "all" ? "bg-[#0094c8] text-white shadow-2xs" : "text-slate-400 hover:text-slate-700 hover:bg-slate-200/60"
                        }`}
                        title="Filter Age"
                      >
                        <Filter className="w-3 h-3" />
                      </button>
                    </div>
                    {openColumnFilter === "age" && (
                      <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-slate-200 rounded-xl shadow-xl p-2 z-30 text-xs normal-case font-normal text-slate-700 space-y-1 text-left animate-scale-up">
                        <div className="font-bold text-[10px] uppercase tracking-wider text-slate-400 px-2 py-1 flex items-center justify-between">
                          <span>Age Filter</span>
                          {columnFilterAge !== "all" && (
                            <button type="button" onClick={() => setColumnFilterAge("all")} className="text-[#0094c8] hover:underline text-[10px] font-bold">Clear</button>
                          )}
                        </div>
                        {[
                          { id: "all", label: "All Ages" },
                          { id: "minor", label: "Minors (< 18 yrs)" },
                          { id: "adult", label: "Adults (18+ yrs)" },
                        ].map(a => (
                          <button
                            key={a.id}
                            type="button"
                            onClick={() => { setColumnFilterAge(a.id as any); setOpenColumnFilter(null); }}
                            className={`w-full text-left px-2.5 py-1.5 rounded-lg flex items-center justify-between hover:bg-slate-50 ${
                              columnFilterAge === a.id ? "font-bold text-[#0094c8] bg-sky-50" : "text-slate-600"
                            }`}
                          >
                            <span>{a.label}</span>
                            {columnFilterAge === a.id && <Check className="w-3.5 h-3.5 text-[#0094c8]" />}
                          </button>
                        ))}
                      </div>
                    )}
                  </th>

                  {/* 7. VERIFIED */}
                  <th className="py-3 px-3 text-center w-24 relative" title="Account Verification Status">
                    <div className="flex items-center justify-center gap-1">
                      <span>Verified</span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setOpenColumnFilter(openColumnFilter === "verified" ? null : "verified"); }}
                        className={`p-1 rounded-md transition-all cursor-pointer ${
                          columnFilterVerified !== "all" ? "bg-[#0094c8] text-white shadow-2xs" : "text-slate-400 hover:text-slate-700 hover:bg-slate-200/60"
                        }`}
                        title="Filter Verification"
                      >
                        <Filter className="w-3 h-3" />
                      </button>
                    </div>
                    {openColumnFilter === "verified" && (
                      <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-slate-200 rounded-xl shadow-xl p-2 z-30 text-xs normal-case font-normal text-slate-700 space-y-1 text-left animate-scale-up">
                        <div className="font-bold text-[10px] uppercase tracking-wider text-slate-400 px-2 py-1 flex items-center justify-between">
                          <span>Verification Status</span>
                          {columnFilterVerified !== "all" && (
                            <button type="button" onClick={() => setColumnFilterVerified("all")} className="text-[#0094c8] hover:underline text-[10px] font-bold">Clear</button>
                          )}
                        </div>
                        {[
                          { id: "all", label: "All Statuses" },
                          { id: "verified", label: "Verified Only" },
                          { id: "unverified", label: "Pending Verification" },
                        ].map(v => (
                          <button
                            key={v.id}
                            type="button"
                            onClick={() => { setColumnFilterVerified(v.id as any); setOpenColumnFilter(null); }}
                            className={`w-full text-left px-2.5 py-1.5 rounded-lg flex items-center justify-between hover:bg-slate-50 ${
                              columnFilterVerified === v.id ? "font-bold text-[#0094c8] bg-sky-50" : "text-slate-600"
                            }`}
                          >
                            <span>{v.label}</span>
                            {columnFilterVerified === v.id && <Check className="w-3.5 h-3.5 text-[#0094c8]" />}
                          </button>
                        ))}
                      </div>
                    )}
                  </th>

                  {/* 8. ACTIONS / RESET */}
                  <th className="py-3 px-4 text-right w-20">
                    <div className="flex items-center justify-end gap-1">
                      {hasActiveColumnFilters ? (
                        <button
                          type="button"
                          onClick={resetAllColumnFilters}
                          className="px-2 py-1 rounded bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200 text-[9px] font-black uppercase tracking-wider transition-all flex items-center space-x-1 cursor-pointer shadow-3xs"
                          title="Reset All Column Filters"
                        >
                          <RotateCcw className="w-2.5 h-2.5" />
                          <span>Reset</span>
                        </button>
                      ) : (
                        <span>Actions</span>
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs">
                {filteredAndSortedUsers.map((user) => {
                  const uidStr = user.uid || (user as any).id || '';
                  const roleStr = user.role || 'student';
                  const isStudent = roleStr === 'student';
                  const isParent = roleStr === 'parent';
                  const isOrg = roleStr === 'organization';
                  const age = calculateAge(user.dateOfBirth);
                  const isUnder18 = age !== null ? age < 18 : null;
                  const ferpaInfo = getUserFerpaInfo(user);

                  // Affiliation text
                  let affiliationTitle = user.school || 'Unassigned';
                  let affiliationSub = user.district || '';
                  if (isOrg) {
                    affiliationTitle = user.orgName || 'Employer Org';
                    affiliationSub = user.isOrgLead ? 'Designated Lead' : 'Team Member';
                  } else if (isParent) {
                    const linked = (users || []).filter(u => 
                      u.role === 'student' && (
                        u.parentId === uidStr || 
                        u.parentEmail === user.email || 
                        u.ferpaConsentRecipientEmail === user.email
                      )
                    );
                    affiliationTitle = linked.length > 0 ? `${linked.map(s => s.name).join(', ')}` : 'No Students Linked';
                    affiliationSub = linked.length > 0 ? (linked[0].school || 'Student') : 'Awaiting Student Link';
                  }

                  return (
                    <tr 
                      key={uidStr}
                      onClick={() => setSelectedProfileUser(user)}
                      className="hover:bg-slate-50/80 transition-colors cursor-pointer group"
                    >
                      {/* 1. User / Contact */}
                      <td className="py-3 px-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-11 h-11 rounded-xl overflow-hidden bg-white border border-slate-200 shrink-0 shadow-xs flex items-center justify-center">
                            <StudentAvatar studentId={uidStr} studentName={user.name} studentEmail={user.email} size="sm" />
                          </div>
                          <div className="min-w-0">
                            <div className="font-black text-slate-900 group-hover:text-[#0094c8] transition-colors flex items-center gap-1.5">
                              <span>{formatUserDisplayName(user.name, user)}</span>
                              {isOrg && user.isOrgLead && (
                                <Crown className="w-3 h-3 text-amber-500 shrink-0" title="Organization Lead" />
                              )}
                            </div>
                            <div className="text-[10px] text-slate-400 truncate max-w-[180px]">{user.email}</div>
                          </div>
                        </div>
                      </td>

                      {/* 2. Role */}
                      <td className="py-3 px-4">
                        <span className={`px-2 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-wider border inline-block ${
                          roleStr === 'student' ? 'bg-sky-50 text-sky-700 border-sky-200' :
                          roleStr === 'advisor' ? 'bg-indigo-50 text-indigo-700 border-indigo-200' :
                          roleStr === 'parent' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                          roleStr === 'organization' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                          'bg-purple-50 text-purple-700 border-purple-200'
                        }`}>
                          {roleStr === 'organization' ? (user.isOrgLead ? 'Employer (Lead)' : 'Employer') : roleStr}
                        </span>
                      </td>

                      {/* 3. Region */}
                      <td className="py-3 px-4">
                        <span className="px-2 py-0.5 rounded text-[9px] font-bold text-slate-600 bg-slate-100 uppercase">
                          {user.tenantId || 'global'}
                        </span>
                      </td>

                      {/* 4. Affiliation */}
                      <td className="py-3 px-4">
                        <div className="font-bold text-slate-800 text-xs truncate max-w-[200px]" title={affiliationTitle}>
                          {affiliationTitle}
                        </div>
                        {affiliationSub && (
                          <div className="text-[10px] text-slate-400 uppercase truncate max-w-[200px]">
                            {affiliationSub}
                          </div>
                        )}
                      </td>

                      {/* 5. FERPA Status (Icon / Symbol Only with Tooltip) */}
                      <td className="py-3 px-3 text-center">
                        {isStudent ? (
                          ferpaInfo.isGranted ? (
                            <span title="FERPA Granted (Signed & Active)" className="inline-flex items-center justify-center p-1 rounded-md text-emerald-600 bg-emerald-50/70 border border-emerald-200/60 shadow-3xs">
                              <ShieldCheck className="w-4 h-4" />
                            </span>
                          ) : ferpaInfo.isSent ? (
                            <span title="Consent Sent (Awaiting Signature)" className="inline-flex items-center justify-center p-1 rounded-md text-amber-600 bg-amber-50/70 border border-amber-200/60 shadow-3xs">
                              <Clock className="w-4 h-4" />
                            </span>
                          ) : (
                            <span title="FERPA Pending" className="inline-flex items-center justify-center p-1 rounded-md text-slate-400 bg-slate-100/70 border border-slate-200/60">
                              <AlertTriangle className="w-4 h-4" />
                            </span>
                          )
                        ) : isParent ? (
                          ferpaInfo.isGranted ? (
                            <span title="FERPA Signed" className="inline-flex items-center justify-center p-1 rounded-md text-emerald-600 bg-emerald-50/70 border border-emerald-200/60 shadow-3xs">
                              <Check className="w-4 h-4 stroke-[2.5]" />
                            </span>
                          ) : (
                            <span title="FERPA Not Signed" className="inline-flex items-center justify-center p-1 rounded-md text-amber-600 bg-amber-50/70 border border-amber-200/60 shadow-3xs">
                              <X className="w-4 h-4 stroke-[2.5]" />
                            </span>
                          )
                        ) : (
                          <span title="Staff / Not Applicable" className="text-slate-300 font-bold text-xs select-none">
                            —
                          </span>
                        )}
                      </td>

                      {/* 6. Age Standing (Color-Coded Age Number) */}
                      <td className="py-3 px-3 text-center">
                        {isStudent ? (
                          age !== null ? (
                            <span 
                              title={isUnder18 ? `Minor (${age} years old)` : `Adult 18+ (${age} years old)`}
                              className={`font-black text-xs px-1.5 py-0.5 rounded ${
                                isUnder18 
                                  ? 'text-amber-600 bg-amber-50 border border-amber-200/60' 
                                  : 'text-emerald-600 bg-emerald-50 border border-emerald-200/60'
                              }`}
                            >
                              {age}
                            </span>
                          ) : (
                            <span title="Date of Birth not set" className="text-slate-300 font-bold text-xs select-none">
                              —
                            </span>
                          )
                        ) : (
                          <span title="Not Applicable" className="text-slate-300 font-bold text-xs select-none">
                            —
                          </span>
                        )}
                      </td>

                      {/* 7. Verification (Check or Gray Clock) */}
                      <td className="py-3 px-3 text-center">
                        {user.verified ? (
                          <span title="Account Verified" className="inline-flex items-center justify-center text-emerald-600">
                            <CheckCircle2 className="w-4 h-4" />
                          </span>
                        ) : (
                          <span title="Pending Verification" className="inline-flex items-center justify-center text-slate-400">
                            <Clock className="w-4 h-4" />
                          </span>
                        )}
                      </td>

                      {/* 8. Actions */}
                      <td className="py-3 px-4 text-right">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedProfileUser(user);
                          }}
                          title="View Profile"
                          className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-slate-400 hover:text-[#0094c8] hover:bg-slate-100 transition-colors cursor-pointer"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* CARD VIEW (Grouped by High School or Flat) */
        activeUserTab === 'students' && groupByHighSchool ? (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-slate-50 border border-slate-200/80 p-3 rounded-2xl">
              <div className="flex items-center space-x-2 text-xs font-bold text-slate-600">
                <GraduationCap className="w-4 h-4 text-[#0094c8]" />
                <span>
                  Grouped by High School: <strong className="text-[#011d3c]">{filteredAndSortedUsers.filter(u => u.role === 'student').length}</strong> Students across <strong className="text-[#011d3c]">{studentsGroupedBySchool.length}</strong> High Schools
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleExpandAllSchools}
                  className="px-2.5 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider bg-white text-slate-600 border border-slate-200 hover:bg-slate-100 transition-all cursor-pointer"
                >
                  Expand All
                </button>
                <button
                  type="button"
                  onClick={handleCollapseAllSchools}
                  className="px-2.5 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider bg-white text-slate-600 border border-slate-200 hover:bg-slate-100 transition-all cursor-pointer"
                >
                  Collapse All
                </button>
              </div>
            </div>

            {studentsGroupedBySchool.map((group) => {
              const isCollapsed = collapsedSchools[group.schoolName] !== false;
              return (
                <div key={group.schoolName} className="border border-slate-200 rounded-2xl bg-white overflow-hidden shadow-2xs">
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleSchoolCollapse(group.schoolName)}
                    className="w-full flex items-center justify-between p-4 bg-slate-50/60 border-b border-slate-100 hover:bg-slate-100/60 transition-colors cursor-pointer text-left select-none"
                  >
                    <div className="flex items-center space-x-3 min-w-0">
                      <div className="w-9 h-9 rounded-xl bg-[#0094c8]/10 text-[#0094c8] border border-[#0094c8]/20 flex items-center justify-center font-bold shrink-0">
                        <School className="w-4 h-4" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-sm font-black text-[#011d3c] uppercase tracking-wide truncate">
                          {group.schoolName}
                        </h3>
                        <div className="text-[10px] text-slate-500 font-semibold">
                          {group.districtName ? `District: ${group.districtName}` : 'High School Directory'}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3 shrink-0">
                      <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase bg-slate-200/80 text-slate-700">
                        {group.students.length} {group.students.length === 1 ? 'Student' : 'Students'}
                      </span>
                      {isCollapsed ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronUp className="w-4 h-4 text-slate-400" />}
                    </div>
                  </div>

                  {!isCollapsed && (
                    <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {group.students.map((student) => {
                        const ferpaInfo = getUserFerpaInfo(student);
                        return (
                          <div
                            key={student.uid}
                            onClick={() => setSelectedProfileUser(student)}
                            className="p-3.5 bg-slate-50/60 hover:bg-slate-100/80 border border-slate-200 rounded-2xl transition-all cursor-pointer space-y-2.5 shadow-3xs"
                          >
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 rounded-xl overflow-hidden bg-white border border-slate-200 shrink-0">
                                <StudentAvatar studentId={student.uid} studentName={student.name} studentEmail={student.email} />
                              </div>
                              <div className="min-w-0 flex-1">
                                <h4 className="font-black text-slate-900 text-xs truncate">{formatUserDisplayName(student.name, student)}</h4>
                                <p className="text-[10px] text-slate-400 truncate">{student.email}</p>
                              </div>
                            </div>
                            <div className="flex items-center justify-between pt-1 border-t border-slate-200/70">
                              <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase border ${
                                ferpaInfo.isGranted ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                ferpaInfo.isSent ? 'bg-amber-50 text-amber-800 border-amber-200' :
                                'bg-slate-100 text-slate-600 border-slate-200'
                              }`}>
                                {ferpaInfo.isGranted ? 'FERPA Active' : ferpaInfo.isSent ? 'Consent Sent' : 'FERPA Pending'}
                              </span>
                              <span className="text-[9px] font-black uppercase text-[#0094c8] hover:underline">
                                View Profile →
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredAndSortedUsers.map((user) => {
              const ferpaInfo = getUserFerpaInfo(user);
              return (
                <div
                  key={user.uid}
                  onClick={() => setSelectedProfileUser(user)}
                  className="p-4 bg-white hover:bg-slate-50 border border-slate-200 rounded-2xl transition-all cursor-pointer space-y-3 shadow-2xs group"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-11 h-11 rounded-xl overflow-hidden bg-slate-50 border border-slate-200 shrink-0">
                      <StudentAvatar studentId={user.uid} studentName={user.name} studentEmail={user.email} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <h4 className="font-black text-slate-900 text-xs truncate group-hover:text-[#0094c8] transition-colors">
                          {formatUserDisplayName(user.name, user)}
                        </h4>
                        {user.role === 'organization' && user.isOrgLead && (
                          <Crown className="w-3 h-3 text-amber-500 shrink-0" />
                        )}
                      </div>
                      <p className="text-[10px] text-slate-400 truncate">{user.email}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-[10px] bg-slate-50 p-2 rounded-xl border border-slate-100">
                    <div>
                      <span className="text-[8px] font-black uppercase text-slate-400 block">Role</span>
                      <span className="font-bold uppercase text-slate-700">{user.role}</span>
                    </div>
                    <div>
                      <span className="text-[8px] font-black uppercase text-slate-400 block">Affiliation</span>
                      <span className="font-bold text-slate-700 truncate block">
                        {user.school || user.orgName || user.tenantId || 'Unassigned'}
                      </span>
                    </div>
                    <div>
                      <span className="text-[8px] font-black uppercase text-slate-400 block">Created</span>
                      <span className="font-semibold text-slate-600 truncate block">
                        {user.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                      </span>
                    </div>
                    <div>
                      <span className="text-[8px] font-black uppercase text-slate-400 block">Last Active</span>
                      <span className="font-semibold text-slate-600 truncate block">
                        {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-1 border-t border-slate-100">
                    <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase border ${
                      ferpaInfo.isGranted ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                      ferpaInfo.isSent ? 'bg-amber-50 text-amber-800 border-amber-200' :
                      'bg-slate-100 text-slate-600 border-slate-200'
                    }`}>
                      {user.role === 'student' ? (ferpaInfo.isGranted ? 'FERPA Granted' : ferpaInfo.isSent ? 'Consent Sent' : 'FERPA Pending') :
                       user.role === 'parent' ? (ferpaInfo.isGranted ? 'FERPA Signed' : 'Not Signed') :
                       user.verified ? 'Verified' : 'Pending Verification'}
                    </span>
                    <span className="text-[9px] font-black uppercase text-[#0094c8] flex items-center gap-1">
                      <span>Manage</span>
                      <ArrowUpRight className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* User Profile Popup Modal */}
      {selectedProfileUser && (
        <UserProfileModal
          user={selectedProfileUser}
          isOpen={!!selectedProfileUser}
          onClose={() => setSelectedProfileUser(null)}
          onUpdateUser={handleUpdateUser}
          onResetPassword={(u) => setSelectedPasswordUser(u)}
          onAdminOverrideFerpa={(studentId) => setOverrideStudentId(studentId)}
          onResendFerpa={handleResendFerpa}
          onReconcileStudent={handleReconcileSingleStudent}
          onSelectAnotherUser={(u) => setSelectedProfileUser(u)}
          allSchools={allSchools || []}
          allDistricts={allDistricts || []}
          allUsers={users || []}
          tenants={tenants || []}
          isReconciling={isReconcilingFerpa}
        />
      )}

      {/* FERPA Consent Reconciliation Tool Modal */}
      {showReconciliationModal && (
        <FerpaReconciliationModal
          isOpen={showReconciliationModal}
          onClose={() => setShowReconciliationModal(false)}
          onReconcileSuccess={() => {
            // refresh
          }}
        />
      )}

      {/* Administrative Password Management Modal */}
      {selectedPasswordUser && (
        <PasswordManagementModal
          user={selectedPasswordUser}
          isOpen={!!selectedPasswordUser}
          onClose={() => setSelectedPasswordUser(null)}
        />
      )}

      {/* FERPA Legal Override Modal */}
      {overrideStudentId && (
        <FerpaOverrideModal
          studentName={
            (users || []).find(u => u.uid === overrideStudentId || (u as any).id === overrideStudentId)?.name || 'Student'
          }
          onDecline={() => setOverrideStudentId(null)}
          onGrant={async (sigData) => {
            await adminOverrideFerpaConsent(overrideStudentId, sigData);
            setOverrideStudentId(null);
            showToast({
              title: 'FERPA Override Recorded',
              message: 'Administrative statutory override granted.',
              type: 'success'
            });
          }}
        />
      )}
    </div>
  );
};
